/**
 * The global status vocabulary (docs/ux/shell.md, DESIGN.md "Status chip tone
 * map"): the number in tabular figures, a hairline, the engine's label from
 * @beam/core, never a local copy. Parking states change shape and carry an
 * icon so the distinction survives without colour.
 */

import { statusLabel, type Status } from '@beam/core/status';
import { cn } from 'cn';
import { BanIcon, PauseIcon, UserXIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export type ChipTone = 'neutral' | 'done' | 'mine' | 'warning' | 'blush' | 'mauve';

export function toneFor(status: Status, emphasis?: 'mine'): ChipTone {
  if (status === 13) return 'warning';
  if (status === 14) return 'blush';
  if (status === 15) return 'mauve';
  if (emphasis === 'mine') return 'mine';
  if (status === 10 || status === 12) return 'done';
  return 'neutral';
}

export const isParking = (status: Status): boolean => status >= 13;

const TONE_CLASS: Record<ChipTone, string> = {
  neutral: 'bg-beam-cream-deep text-foreground border-beam-line',
  done: 'bg-beam-wash-sage text-beam-sage-ink border-beam-sage',
  mine: 'bg-beam-wash-harbour text-beam-harbour-ink border-beam-harbour',
  warning: 'bg-beam-warning-bg text-beam-warning border-beam-warning/30',
  blush: 'bg-beam-wash-blush text-beam-blush-ink border-beam-blush',
  mauve: 'bg-beam-wash-mauve text-beam-mauve-ink border-beam-mauve',
};

const ICON: Partial<Record<Status, typeof PauseIcon>> = { 13: PauseIcon, 14: UserXIcon, 15: BanIcon };

export type StatusChipProps = {
  status: Status;
  size?: 'sm' | 'md';
  emphasis?: 'mine';
  /** Number only, for the pipeline's status strip; tone and shape preserved. */
  variant?: 'full' | 'number';
  /** A trailing outline chip, for example "Awaiting Transcription" beside 6 or the plaintiff gate. */
  subState?: ReactNode;
  className?: string;
};

export function StatusChip({ status, size = 'sm', emphasis, variant = 'full', subState, className }: StatusChipProps) {
  const tone = toneFor(status, emphasis);
  const shape = isParking(status) ? 'square' : 'pill';
  const Icon = ICON[status];
  const label = statusLabel(status);
  const sizeClass = size === 'sm' ? 'h-[22px] text-xs px-2 gap-1.5' : 'h-7 text-[13px] px-2.5 gap-2';
  return (
    <span className={cn('inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-1', className)}>
      <span
        data-slot="status-chip"
        data-status={status}
        data-tone={tone}
        data-shape={shape}
        className={cn(
          'inline-flex shrink-0 items-center whitespace-nowrap border font-medium leading-none',
          shape === 'pill' ? 'rounded-full' : 'rounded-[6px]',
          TONE_CLASS[tone],
          sizeClass,
        )}
      >
        <span className="tabular font-semibold">{status}</span>
        {variant === 'full' ? (
          <>
            <span aria-hidden="true" className="h-3 w-px bg-current opacity-40" />
            {Icon ? <Icon aria-hidden="true" className="size-3" /> : null}
            <span>{label}</span>
          </>
        ) : (
          <span className="sr-only">{label}</span>
        )}
      </span>
      {subState ? (
        <span
          data-slot="status-substate"
          className={cn('inline-flex shrink-0 items-center rounded-full border border-beam-line-strong bg-beam-paper text-foreground whitespace-nowrap', size === 'sm' ? 'h-[22px] px-2 text-xs' : 'h-7 px-2.5 text-[13px]')}
        >
          {subState}
        </span>
      ) : null}
    </span>
  );
}
